// src/lib/doctorsDirectory.js
import { getSupabaseBaseUrl, supabase } from "@/services/supabaseClient";
import { AFRIMED_MARKETS } from "@/config/markets.js";
import { formatClinicianSpecialtyLabel } from "@/utils/clinicianPresentation.js";

/**
 * Single source of truth for browsing doctors in Appointments.
 *
 * DEV mode (VITE_BYPASS_AUTH=true):
 *   - Reads ONLY from v_doctor_directory_browse_dev (seed data)
 *   - No auth session required
 *
 * Normal mode:
 *   - Reads ONLY from v_doctor_directory_browse (real browse view)
 *   - Requires auth session (authenticated role)
 *
 * IMPORTANT:
 *   - Never throw in UI browse flows (avoid red "permission denied" in dev).
 *   - Return [] when not permitted / not logged in.
 */

function isBypassAuth() {
  const bypass = String(import.meta.env.VITE_BYPASS_AUTH || "").toLowerCase() === "true";
  if (!bypass) return false;

  if (typeof window === "undefined") return false;
  const hostname = String(window.location.hostname || "").trim().toLowerCase();
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  );
}

function looksLikePermissionDenied(err) {
  const msg = String(err?.message || "").toLowerCase();
  // Postgres permission denied errors often include this phrase
  return msg.includes("permission denied");
}

function normalizeSpecialtyLabel(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[\/_-]+/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function displaySpecialtyLabel(value) {
  return formatClinicianSpecialtyLabel(value, "");
}

function specialtyMatches(rowSpecialty, selectedSpecialty) {
  const rowNorm = normalizeSpecialtyLabel(rowSpecialty);
  const selectedNorm = normalizeSpecialtyLabel(selectedSpecialty);
  if (!rowNorm || !selectedNorm) return false;
  return (
    rowNorm === selectedNorm ||
    rowNorm.includes(selectedNorm) ||
    selectedNorm.includes(rowNorm)
  );
}

function toArray(v) {
  if (Array.isArray(v)) return v;
  if (v == null) return [];
  const s = String(v).trim();
  return s ? [s] : [];
}

function arrayFromUnknown(v) {
  if (Array.isArray(v)) {
    return v.map((x) => String(x || "").trim()).filter(Boolean);
  }
  if (v == null) return [];
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return [];
    if (s.startsWith("[") && s.endsWith("]")) {
      try {
        const parsed = JSON.parse(s);
        return arrayFromUnknown(parsed);
      } catch {
        // ignore malformed JSON-like strings
      }
    }
    if (s.includes(",")) {
      return s
        .split(",")
        .map((x) => String(x || "").trim())
        .filter(Boolean);
    }
    return [s];
  }
  return toArray(v).map((x) => String(x || "").trim()).filter(Boolean);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const str = String(value).trim();
    if (str) return str;
  }
  return "";
}

const PRACTICE_MODE_ALIASES = {
  virtual: "virtual_only",
  physical: "in_person_only",
};
const PRACTICE_MODE_VALUES = new Set(["virtual_only", "in_person_only", "hybrid"]);

const COUNTRY_ALIASES = {
  cd: "DR Congo",
  cod: "DR Congo",
  congo: "DR Congo",
  "congo kinshasa": "DR Congo",
  "congo drc": "DR Congo",
  "democratic republic of congo": "DR Congo",
  "democratic republic of the congo": "DR Congo",
  drc: "DR Congo",
  "dr congo": "DR Congo",
  "d r congo": "DR Congo",
  "d r c": "DR Congo",
  kinshasa: "DR Congo",
};

const MARKET_COUNTRY_NAMES = AFRIMED_MARKETS
  .map((market) => String(market?.name || "").trim())
  .filter(Boolean);

const DIAL_CODE_COUNTRIES = [
  ["+243", "DR Congo"],
  ["+260", "Zambia"],
  ["+263", "Zimbabwe"],
  ["+237", "Cameroon"],
  ["+233", "Ghana"],
  ["+255", "Tanzania"],
  ["+267", "Botswana"],
  ["+27", "South Africa"],
];

const UNAMBIGUOUS_CURRENCY_COUNTRIES = {
  ZMW: "Zambia",
};

function normalizePracticeMode(value) {
  const raw = String(value || "").trim();
  const canonical = PRACTICE_MODE_ALIASES[raw] || raw;
  return PRACTICE_MODE_VALUES.has(canonical) ? canonical : "";
}

function normalizeCountryText(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (COUNTRY_ALIASES[key]) return COUNTRY_ALIASES[key];
  const marketMatch = MARKET_COUNTRY_NAMES.find((name) => {
    const normalizedName = name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return key === normalizedName || key.includes(normalizedName);
  });
  return marketMatch || raw;
}

function payoutProfileAddress(row = {}) {
  const payoutMeta = row?.payout_meta && typeof row.payout_meta === "object" ? row.payout_meta : {};
  return payoutMeta.profile_address && typeof payoutMeta.profile_address === "object"
    ? payoutMeta.profile_address
    : {};
}

function resolveCountryFromPhone(value) {
  const digits = String(value || "").replace(/[^\d+]/g, "");
  if (!digits) return "";
  const normalized = digits.startsWith("+") ? digits : `+${digits}`;
  const match = DIAL_CODE_COUNTRIES.find(([dialCode]) => normalized.startsWith(dialCode));
  return match?.[1] || "";
}

function resolveCountryFromCurrency(...values) {
  for (const value of values) {
    const currency = String(value || "").trim().toUpperCase();
    if (!currency) continue;
    if (UNAMBIGUOUS_CURRENCY_COUNTRIES[currency]) return UNAMBIGUOUS_CURRENCY_COUNTRIES[currency];
  }
  return "";
}

function parseFeesObject(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  const raw = String(value || "").trim();
  if (!raw || !raw.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function resolveDoctorOperatingCountry(row = {}) {
  const payoutMeta = row?.payout_meta && typeof row.payout_meta === "object" ? row.payout_meta : {};
  const profileAddress = payoutProfileAddress(row);
  const explicitCountry = normalizeCountryText(
    firstNonEmpty(
      row?.operating_country,
      row?.practice_country,
      row?.primary_practice_country,
      row?.facility_country,
      row?.country,
      payoutMeta.payment_setup_country,
      profileAddress.country,
      profileAddress.country_code,
      row?.register_country,
      row?.registration_country,
      row?.market_country,
    )
  );
  if (explicitCountry) return explicitCountry;
  const phoneCountry = resolveCountryFromPhone(
    firstNonEmpty(row?.phone, row?.phone_number, row?.mobile_phone, row?.contact_phone)
  );
  if (phoneCountry) return phoneCountry;
  const fees = parseFeesObject(row?.consultation_fees);
  return resolveCountryFromCurrency(
    row?.service_currency,
    row?.currency,
    pickCurrencyFromFeesObject(fees)
  );
}

function hasMeaningfulPhysicalLocation(row = {}) {
  return !!firstNonEmpty(
    row?.facility_address,
    row?.facility_city,
    row?.facility_name && String(row.facility_name).trim().toLowerCase() !== "facility"
      ? row.facility_name
      : "",
    row?.address,
    row?.street_address,
  );
}

function hasTelehealthReadiness(row = {}) {
  return (
    row?.evisit_enabled === true ||
    !!row?.evisit_availability ||
    row?.accepts_evisit === true
  );
}

function toNumberOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickDoctorDisplayName(row = {}) {
  const fromNames = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  const fromEmail =
    typeof row.email === "string" && row.email.includes("@")
      ? row.email.split("@")[0]
      : "";
  return (
    row.display_name ||
    row.full_name ||
    fromNames ||
    fromEmail ||
    "Doctor"
  );
}

function doctorSpecialtyCandidates(row = {}) {
  const candidates = [
    row.specialty,
    row.primary_specialty,
    ...arrayFromUnknown(row.specialties),
    ...arrayFromUnknown(row.specialization),
    ...arrayFromUnknown(row.specialty_aliases),
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  return Array.from(new Set(candidates));
}

function doctorMatchesSearch(row = {}, search = "") {
  const s = String(search || "").trim().toLowerCase();
  if (!s) return true;

  const hay = [
    row.display_name,
    row.full_name,
    row.first_name,
    row.last_name,
    row.specialty,
    row.primary_specialty,
    ...arrayFromUnknown(row.specialties),
    ...arrayFromUnknown(row.specialization),
    ...arrayFromUnknown(row.languages_spoken),
    ...arrayFromUnknown(row.languages),
    row.facility_name,
    row.facility_city,
    row.facility_country,
    row.operating_country,
    resolveDoctorOperatingCountry(row),
    row.display_location,
    row.city,
    row.country,
    row.address,
    row.facility_address,
  ]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");

  return hay.includes(s);
}

function hasDoctorProfileBasics(row = {}) {
  const name = firstNonEmpty(row?.full_name, row?.display_name, row?.first_name, row?.last_name);
  const specialty = firstNonEmpty(
    row?.primary_specialty,
    row?.specialty,
    ...arrayFromUnknown(row?.specialties),
    ...arrayFromUnknown(row?.specialization)
  );
  return !!name && !!specialty;
}

function hasDoctorProfileHeadshot(row = {}) {
  const candidate = firstNonEmpty(
    row?.profile_photo_path,
    row?.profile_image_url,
    row?.profile_photo_url,
    row?.avatar_url
  );
  if (!candidate) return false;
  return !/profile_avatar|avatar_placeholder|circular-avatar-placeholder/i.test(candidate);
}

// Shared public clinician-avatar resolution used by referral discovery.
// The clinician profile remains the authority; callers choose presentation.
export function resolveDoctorAvatarSrc(row = {}, fallback = "") {
  const path = String(row?.profile_photo_path || "").trim();
  if (path) {
    if (/^https?:\/\//i.test(path)) return path;
    const base = getSupabaseBaseUrl();
    if (base) return `${base}/storage/v1/object/public/doctor-profile-images/${path}`;
  }

  // A current storage path wins over a legacy URL.  Uploads replace the path
  // first, while legacy URLs can remain cached after a clinician changes a
  // headshot.
  const profileImageUrl = String(row?.profile_image_url || "").trim();
  if (profileImageUrl) return profileImageUrl;

  return fallback;
}

function isDiscoverableDoctorProfile(row = {}) {
  const status = String(row?.verified_status || "").trim().toLowerCase();
  return (
    status === "verified" ||
    row?.onboarding_completed === true ||
    hasDoctorProfileBasics(row)
  );
}

export function isInternalOrSmokeDoctor(row = {}) {
  const classification = String(row?.account_classification || row?.profile_classification || "")
    .trim()
    .toLowerCase();
  const hay = [
    row?.email,
    row?.display_name,
    row?.full_name,
    row?.first_name,
    row?.last_name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .join(" ");

  return (
    classification === "internal" ||
    classification === "test" ||
    classification === "smoke" ||
    row?.exclude_from_public_discovery === true ||
    row?.public_discovery_excluded === true ||
    row?.is_demo === true ||
    hay.includes("@afrimed.test") ||
    hay.includes("codex smoke") ||
    hay.includes("smoke doctor") ||
    hay.includes("ehr smoke")
  );
}

function hasPositivePrice(value) {
  const numeric = toNumberOrNull(value);
  return Number.isFinite(numeric) && numeric > 0;
}

export function isReadyForPatientDiscovery(row = {}) {
  const status = String(row?.verified_status || "").trim().toLowerCase();
  const stagedOrVerified =
    status === "verified" ||
    row?.onboarding_completed === true ||
    row?.staging_deferred_verification === true;
  const mode = normalizePracticeMode(row?.practice_mode);
  const operatingCountry = resolveDoctorOperatingCountry(row);
  const needsPhysicalLocation = mode === "in_person_only" || mode === "hybrid";
  const needsTelehealth = mode !== "in_person_only";
  const fees = parseFeesObject(row?.consultation_fees);
  const hasConfiguredVirtualFee =
    (row?.evisit_enabled === true || fees?.evisit_enabled === true) &&
    hasPositivePrice(fees?.evisit_price ?? fees?.e_visit_price);
  const hasServiceLabel =
    firstNonEmpty(row?.service_label, row?.primary_service_label, row?.service_name) ||
    (hasConfiguredVirtualFee ? "E-Visit" : "");
  const hasPrice =
    hasPositivePrice(row?.service_price ?? row?.primary_service_price ?? row?.consultation_fee) ||
    hasConfiguredVirtualFee;

  return (
    stagedOrVerified &&
    !isInternalOrSmokeDoctor(row) &&
    hasDoctorProfileBasics(row) &&
    hasDoctorProfileHeadshot(row) &&
    !!operatingCountry &&
    !!hasServiceLabel &&
    hasPrice &&
    (!needsPhysicalLocation || hasMeaningfulPhysicalLocation(row)) &&
    (!needsTelehealth || hasTelehealthReadiness(row))
  );
}

function isCanonicalBrowseCardReady(row = {}) {
  const doctorId = String(row?.doctor_user_id || row?.user_id || "").trim();
  const status = String(row?.verified_status || "").trim().toLowerCase();
  const stagedOrVerified =
    status === "verified" ||
    row?.onboarding_completed === true ||
    row?.staging_deferred_verification === true;
  return (
    !!doctorId &&
    stagedOrVerified &&
    !isInternalOrSmokeDoctor(row) &&
    hasDoctorProfileBasics(row) &&
    hasDoctorProfileHeadshot(row)
  );
}

function humanizeServiceCode(code = "") {
  const raw = String(code || "").trim();
  if (!raw) return "";
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function isMeaningfulValue(value) {
  if (value == null) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 && trimmed !== "—";
  }
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function isGenericDoctorPlaceholder(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return true;
  return v === "doctor" || v === "dr" || v === "dr.";
}

function isMeaningfulValueForKey(key, value) {
  if (!isMeaningfulValue(value)) return false;
  const lowerKey = String(key || "").toLowerCase();
  if (
    lowerKey === "specialty" ||
    lowerKey === "primary_specialty" ||
    lowerKey === "display_name" ||
    lowerKey === "full_name"
  ) {
    return !isGenericDoctorPlaceholder(value);
  }
  return true;
}

function mergePreferPrimaryRow(primaryRow = {}, secondaryRow = {}) {
  const merged = { ...(secondaryRow || {}), ...(primaryRow || {}) };
  for (const [key, value] of Object.entries(primaryRow || {})) {
    if (isMeaningfulValueForKey(key, value)) continue;
    const fallbackValue = secondaryRow?.[key];
    if (isMeaningfulValueForKey(key, fallbackValue)) {
      merged[key] = fallbackValue;
    }
  }
  return merged;
}

function pickFeeFromFeesObject(fees) {
  if (!fees || typeof fees !== "object") return null;
  const candidates = [
    fees.consultation_fee,
    fees.consultationPrice,
    fees.consultation_price,
    fees.primary_consultation_fee,
    fees.primary_fee,
    fees.default_fee,
    fees.default_price,
    fees.evisit_price,
    fees.e_visit_price,
    fees.in_person_price,
    fees.price,
  ];
  for (const value of candidates) {
    const n = toNumberOrNull(value);
    if (n != null) return n;
  }
  return null;
}

function pickCurrencyFromFeesObject(fees) {
  if (!fees || typeof fees !== "object") return "";
  const candidates = [
    fees.currency,
    fees.consultation_currency,
    fees.default_currency,
    fees.evisit_currency,
    fees.e_visit_currency,
    fees.in_person_currency,
  ];
  return firstNonEmpty(...candidates);
}

function mergeDoctorRows(primaryRows = [], secondaryRows = []) {
  const byId = new Map();

  for (const row of secondaryRows || []) {
    const id = String(row?.doctor_user_id || "").trim();
    if (!id) continue;
    byId.set(id, row);
  }

  for (const row of primaryRows || []) {
    const id = String(row?.doctor_user_id || "").trim();
    if (!id) continue;
    const prev = byId.get(id);
    byId.set(id, prev ? mergePreferPrimaryRow(row, prev) : row);
  }

  return Array.from(byId.values());
}

async function filterToDoctorRoleRows(rows = []) {
  const doctorIds = Array.from(
    new Set(
      (Array.isArray(rows) ? rows : [])
        .map((r) => String(r?.doctor_user_id || r?.user_id || "").trim())
        .filter(Boolean)
    )
  );
  if (!doctorIds.length) return Array.isArray(rows) ? rows : [];

  const [{ data: roleRows, error: roleError }, { data: profileRows }, { data: doctorRows }] =
    await Promise.all([
      supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "doctor")
        .in("user_id", doctorIds),
      supabase
        .from("profiles")
        .select("user_id,role,role_selected,account_classification,exclude_from_public_discovery")
        .in("user_id", doctorIds),
      supabase
        .from("doctor_profiles")
        .select("user_id,onboarding_completed,primary_specialty,specialty,specialties,full_name,public_discovery_excluded")
        .in("user_id", doctorIds),
    ]);

  if (roleError && !Array.isArray(profileRows) && !Array.isArray(doctorRows)) {
    return Array.isArray(rows) ? rows : [];
  }

  const roleDoctorIds = new Set(
    (Array.isArray(roleRows) ? roleRows : [])
      .map((r) => String(r?.user_id || "").trim())
      .filter(Boolean)
  );
  for (const row of Array.isArray(profileRows) ? profileRows : []) {
    const role = String(row?.role || "").trim().toLowerCase();
    if (role === "doctor" || role === "clinician") {
      const userId = String(row?.user_id || "").trim();
      if (userId) roleDoctorIds.add(userId);
    }
  }
  for (const row of Array.isArray(doctorRows) ? doctorRows : []) {
    const userId = String(row?.user_id || "").trim();
    if (userId) roleDoctorIds.add(userId);
  }

  const accountByUserId = new Map(
    (Array.isArray(profileRows) ? profileRows : [])
      .map((row) => [String(row?.user_id || "").trim(), row])
      .filter(([userId]) => Boolean(userId))
  );
  const doctorByUserId = new Map(
    (Array.isArray(doctorRows) ? doctorRows : [])
      .map((row) => [String(row?.user_id || "").trim(), row])
      .filter(([userId]) => Boolean(userId))
  );

  return (Array.isArray(rows) ? rows : [])
    .filter((row) =>
      roleDoctorIds.has(String(row?.doctor_user_id || row?.user_id || "").trim())
    )
    .map((row) => {
      const userId = String(row?.doctor_user_id || row?.user_id || "").trim();
      const account = accountByUserId.get(userId) || {};
      const doctor = doctorByUserId.get(userId) || {};
      return {
        ...row,
        onboarding_completed:
          doctor?.onboarding_completed ?? row?.onboarding_completed ?? false,
        account_classification:
          account?.account_classification ?? row?.account_classification ?? null,
        exclude_from_public_discovery:
          account?.exclude_from_public_discovery ??
          row?.exclude_from_public_discovery ??
          false,
        public_discovery_excluded:
          doctor?.public_discovery_excluded ??
          row?.public_discovery_excluded ??
          false,
      };
    });
}

async function fetchDoctorServicesMap(doctorIds = []) {
  if (!doctorIds.length) return new Map();

  const out = new Map();

  // Primary source: doctor_services (used by profile settings + doctor profile)
  const { data: directRows } = await supabase
    .from("doctor_services")
    .select("doctor_id,name,price,currency,duration_minutes,is_active,sort_order,updated_at,created_at")
    .in("doctor_id", doctorIds);

  const directGrouped = new Map();
  for (const row of Array.isArray(directRows) ? directRows : []) {
    if (row?.is_active === false) continue;
    const doctorId = String(row?.doctor_id || "").trim();
    if (!doctorId) continue;
    if (!directGrouped.has(doctorId)) directGrouped.set(doctorId, []);
    directGrouped.get(doctorId).push(row);
  }

  for (const [doctorId, rows] of directGrouped.entries()) {
    const ordered = rows
      .slice()
      .sort((a, b) => {
        const aSort = Number(a?.sort_order);
        const bSort = Number(b?.sort_order);
        if (Number.isFinite(aSort) || Number.isFinite(bSort)) {
          const aa = Number.isFinite(aSort) ? aSort : Number.MAX_SAFE_INTEGER;
          const bb = Number.isFinite(bSort) ? bSort : Number.MAX_SAFE_INTEGER;
          if (aa !== bb) return aa - bb;
        }
        const aTs = Date.parse(a?.updated_at || a?.created_at || "") || 0;
        const bTs = Date.parse(b?.updated_at || b?.created_at || "") || 0;
        return bTs - aTs;
      })
      .slice(0, 2)
      .map((r) => ({
        label: String(r?.name || "").trim() || "Service",
        price: r?.price ?? null,
        currency: String(r?.currency || "").trim() || null,
        durationMinutes: Number(r?.duration_minutes || 30) || 30,
      }));

    out.set(doctorId, {
      primary: ordered[0] || null,
      secondary: ordered[1] || null,
    });
  }

  // Backward-compat source: doctor_service_prices (only fill missing doctors)
  const missingDoctorIds = doctorIds.filter((id) => !out.has(id));
  if (!missingDoctorIds.length) return out;

  const { data: prices, error: pricesError } = await supabase
    .from("doctor_service_prices")
    .select("doctor_user_id,service_code,price,currency,is_active,created_at,updated_at")
    .eq("is_active", true)
    .in("doctor_user_id", missingDoctorIds);

  if (pricesError || !Array.isArray(prices) || !prices.length) return out;

  const serviceCodes = Array.from(
    new Set(prices.map((r) => String(r?.service_code || "").trim()).filter(Boolean))
  );

  const { data: catalogRows } = await supabase
    .from("services_catalog")
    .select("code,name")
    .in("code", serviceCodes);

  const nameByCode = new Map(
    (Array.isArray(catalogRows) ? catalogRows : [])
      .map((r) => [String(r?.code || "").trim(), String(r?.name || "").trim()])
      .filter(([code]) => !!code)
  );

  const grouped = new Map();
  for (const row of prices) {
    const doctorId = String(row?.doctor_user_id || "").trim();
    if (!doctorId) continue;
    if (!grouped.has(doctorId)) grouped.set(doctorId, []);
    grouped.get(doctorId).push(row);
  }

  for (const [doctorId, rows] of grouped.entries()) {
    const ordered = rows
      .slice()
      .sort((a, b) => {
        const aTs = Date.parse(a?.updated_at || a?.created_at || "") || 0;
        const bTs = Date.parse(b?.updated_at || b?.created_at || "") || 0;
        return bTs - aTs;
      })
      .slice(0, 2)
      .map((r) => {
        const code = String(r?.service_code || "").trim();
        const name = nameByCode.get(code) || humanizeServiceCode(code) || "Service";
        return {
          label: name,
          price: r?.price ?? null,
          currency: String(r?.currency || "").trim() || null,
        };
      });

    out.set(doctorId, {
      primary: ordered[0] || null,
      secondary: ordered[1] || null,
    });
  }

  return out;
}

async function enrichDoctorRows(rows = []) {
  const doctorIds = Array.from(
    new Set(
      (Array.isArray(rows) ? rows : [])
        .map((r) => String(r?.doctor_user_id || "").trim())
        .filter(Boolean)
    )
  );

  if (!doctorIds.length) return [];

  const [
    { data: profileRows },
    { data: accountRows },
    servicesMap,
    { data: consultancies },
    { data: legacyConsultancies },
  ] = await Promise.all([
    supabase
      .from("doctor_profiles")
      .select("*")
      .in("user_id", doctorIds),
    supabase
      .from("profiles")
      .select("user_id,account_classification,exclude_from_public_discovery")
      .in("user_id", doctorIds),
    fetchDoctorServicesMap(doctorIds),
    supabase
      .from("doctor_consultancies")
      .select("*")
      .in("doctor_id", doctorIds)
      .order("is_primary", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("updated_at", { ascending: false }),
    supabase
      .from("doctor_consulting_locations")
      .select("*")
      .in("doctor_id", doctorIds)
      .order("updated_at", { ascending: false }),
  ]);

  const profileByUserId = new Map(
    (Array.isArray(profileRows) ? profileRows : [])
      .map((r) => [String(r?.user_id || "").trim(), r])
      .filter(([id]) => !!id)
  );
  const accountByUserId = new Map(
    (Array.isArray(accountRows) ? accountRows : [])
      .map((r) => [String(r?.user_id || "").trim(), r])
      .filter(([id]) => !!id)
  );
  const profileIdToUserId = new Map(
    (Array.isArray(profileRows) ? profileRows : [])
      .map((r) => [String(r?.id || "").trim(), String(r?.user_id || "").trim()])
      .filter(([profileId, userId]) => !!profileId && !!userId)
  );
  const expandedDoctorIds = Array.from(
    new Set([
      ...doctorIds,
      ...(Array.isArray(profileRows)
        ? profileRows.map((r) => String(r?.id || "").trim()).filter(Boolean)
        : []),
    ])
  );

  const [{ data: consultanciesExpanded }, { data: legacyExpanded }] = await Promise.all([
    supabase
      .from("doctor_consultancies")
      .select("*")
      .in("doctor_id", expandedDoctorIds)
      .order("is_primary", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("updated_at", { ascending: false }),
    supabase
      .from("doctor_consulting_locations")
      .select("*")
      .in("doctor_id", expandedDoctorIds)
      .order("updated_at", { ascending: false }),
  ]);

  const consultancyByDoctorId = new Map();
  const consultSource =
    Array.isArray(consultanciesExpanded) && consultanciesExpanded.length
      ? consultanciesExpanded
      : Array.isArray(consultancies)
      ? consultancies
      : [];
  for (const row of consultSource) {
    const rawDoctorId = String(row?.doctor_id || "").trim();
    const doctorId = profileIdToUserId.get(rawDoctorId) || rawDoctorId;
    if (!doctorId || consultancyByDoctorId.has(doctorId)) continue;
    consultancyByDoctorId.set(doctorId, row);
  }

  const legacySource =
    Array.isArray(legacyExpanded) && legacyExpanded.length
      ? legacyExpanded
      : Array.isArray(legacyConsultancies)
      ? legacyConsultancies
      : [];
  for (const row of legacySource) {
    const rawDoctorId = String(row?.doctor_id || "").trim();
    const doctorId = profileIdToUserId.get(rawDoctorId) || rawDoctorId;
    if (!doctorId || consultancyByDoctorId.has(doctorId)) continue;
    consultancyByDoctorId.set(doctorId, {
      id: row?.id ?? null,
      doctor_id: doctorId,
      facility_name: row?.facility_name || null,
      facility_address: row?.street_address || null,
      facility_city: row?.city || null,
      facility_country: row?.country || null,
      facility_lat:
        row?.facility_lat ??
        row?.facility_latitude ??
        row?.lat ??
        row?.latitude ??
        null,
      facility_lng:
        row?.facility_lng ??
        row?.facility_longitude ??
        row?.lng ??
        row?.lon ??
        row?.longitude ??
        null,
      is_primary: false,
      sort_order: 0,
      updated_at: row?.updated_at || null,
    });
  }

  return (Array.isArray(rows) ? rows : []).map((row) => {
    const doctorId = String(row?.doctor_user_id || "").trim();
    const profile = profileByUserId.get(doctorId) || null;
    const account = accountByUserId.get(doctorId) || null;
    const services = servicesMap.get(doctorId) || null;
    const consultancy = consultancyByDoctorId.get(doctorId) || null;

    const existingSpecialties = toArray(row?.specialties);
    const existingSpecialization = toArray(row?.specialization);
    const profileLanguages = arrayFromUnknown(profile?.languages_spoken);
    const profileLanguagesLegacy = arrayFromUnknown(profile?.languages);
    const rowLanguages = arrayFromUnknown(row?.languages_spoken);
    const rowLanguagesLegacy = arrayFromUnknown(row?.languages);

    const facilityName = firstNonEmpty(
      consultancy?.facility_name,
      row?.facility_name,
      row?.hospital,
      row?.institution_slug
    ) || null;
    const facilityAddress = firstNonEmpty(
      consultancy?.facility_address,
      row?.facility_address,
      row?.address,
      row?.street_address
    ) || null;
    const facilityCity = firstNonEmpty(
      consultancy?.facility_city,
      row?.facility_city,
      row?.city
    ) || null;
    const facilityCountry = firstNonEmpty(
      consultancy?.facility_country,
      row?.facility_country,
      row?.country
    ) || null;
    const operatingCountry = resolveDoctorOperatingCountry({
      ...(profile || {}),
      ...row,
      facility_country: facilityCountry,
    });

    const facilityLat = toNumberOrNull(
      consultancy?.facility_lat ??
        consultancy?.facility_latitude ??
        consultancy?.lat ??
        consultancy?.latitude ??
        row?.facility_lat ??
        row?.facility_latitude ??
        row?.lat ??
        row?.latitude
    );

    const facilityLng = toNumberOrNull(
      consultancy?.facility_lng ??
        consultancy?.facility_longitude ??
        consultancy?.lng ??
        consultancy?.lon ??
        consultancy?.longitude ??
        row?.facility_lng ??
        row?.facility_longitude ??
        row?.lng ??
        row?.lon ??
        row?.longitude
    );

    const consultationFee =
      profile?.consultation_fee ??
      row?.consultation_fee ??
      pickFeeFromFeesObject(profile?.consultation_fees) ??
      pickFeeFromFeesObject(row?.consultation_fees) ??
      null;
    const primaryServicePrice =
      services?.primary?.price ??
      row?.service_price ??
      row?.primary_service_price ??
      consultationFee ??
      null;
    const primaryServiceLabel =
      firstNonEmpty(
        services?.primary?.label,
        row?.service_label,
        row?.primary_service_label,
        row?.service_name
      ) || (primaryServicePrice != null ? "Consultation" : "");

    const secondaryServicePrice =
      services?.secondary?.price ??
      row?.secondary_service_price ??
      row?.service_name_price ??
      null;
    const secondaryServiceLabel = firstNonEmpty(
      services?.secondary?.label,
      row?.secondary_service_label,
      row?.service_name
    );

    return {
      ...row,
      first_name: profile?.first_name || row?.first_name || null,
      last_name: profile?.last_name || row?.last_name || null,
      full_name:
        profile?.full_name ||
        row?.full_name ||
        [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
        [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim() ||
        null,
      display_name:
        profile?.full_name ||
        row?.display_name ||
        row?.full_name ||
        pickDoctorDisplayName(profile || row),
      specialty:
        displaySpecialtyLabel(
          profile?.primary_specialty ||
            profile?.specialty ||
            row?.specialty ||
            doctorSpecialtyCandidates(profile || row)[0] ||
            "Doctor"
        ),
      primary_specialty: profile?.primary_specialty || row?.primary_specialty || null,
      specialties: existingSpecialties.length ? existingSpecialties : toArray(profile?.specialties),
      specialization: existingSpecialization.length
        ? existingSpecialization
        : toArray(profile?.specialization),
      verified_status: profile?.verified_status || row?.verified_status || "pending",
      onboarding_completed:
        profile?.onboarding_completed ??
        row?.onboarding_completed ??
        false,
      account_classification:
        account?.account_classification ??
        profile?.account_classification ??
        row?.account_classification ??
        null,
      exclude_from_public_discovery:
        account?.exclude_from_public_discovery ??
        profile?.exclude_from_public_discovery ??
        row?.exclude_from_public_discovery ??
        false,
      public_discovery_excluded:
        profile?.public_discovery_excluded ?? row?.public_discovery_excluded ?? false,
      email: profile?.email || row?.email || null,
      phone: profile?.phone || row?.phone || null,
      practice_mode: profile?.practice_mode || row?.practice_mode || null,
      payout_meta: profile?.payout_meta || row?.payout_meta || null,
      register_country: profile?.register_country || row?.register_country || null,
      evisit_enabled: profile?.evisit_enabled ?? row?.evisit_enabled ?? false,
      evisit_availability:
        profile?.evisit_availability ?? row?.evisit_availability ?? null,
      staging_deferred_verification:
        Array.isArray(profile?.verification_documents) &&
        profile.verification_documents.some((doc) => doc?.staging_deferred === true),
      profile_photo_path: profile?.profile_photo_path || row?.profile_photo_path || null,
      profile_image_url:
        firstNonEmpty(
          profile?.profile_image_url,
          profile?.profile_photo_url,
          profile?.avatar_url,
        ) || null,
      years_of_experience:
        profile?.years_of_experience ??
        profile?.years_experience ??
        row?.years_of_experience ??
        row?.years_experience ??
        null,
      rating: profile?.rating ?? row?.rating ?? null,
      consultation_fee: consultationFee,
      consultation_fees: profile?.consultation_fees ?? row?.consultation_fees ?? null,
      languages_spoken:
        profileLanguages.length > 0
          ? profileLanguages
          : profileLanguagesLegacy.length > 0
          ? profileLanguagesLegacy
          : rowLanguages.length > 0
          ? rowLanguages
          : rowLanguagesLegacy,
      service_label: primaryServiceLabel,
      service_price: primaryServicePrice,
      secondary_service_label: secondaryServiceLabel,
      secondary_service_price: secondaryServicePrice,
      currency:
        services?.primary?.currency ||
        services?.secondary?.currency ||
        pickCurrencyFromFeesObject(profile?.consultation_fees) ||
        pickCurrencyFromFeesObject(row?.consultation_fees) ||
        row?.currency ||
        row?.service_currency ||
        "",
      consultancy_id: consultancy?.id || row?.consultancy_id || null,
      facility_name: facilityName,
      facility_address: facilityAddress,
      facility_city: facilityCity,
      facility_country: facilityCountry,
      operating_country: operatingCountry,
      facility_lat: facilityLat,
      facility_lng: facilityLng,
      display_location:
        facilityCity ||
        operatingCountry ||
        facilityName ||
        row?.display_location ||
        row?.facility_city ||
        row?.facility_country ||
        row?.facility_name ||
        "—",
      has_facility_details:
        typeof row?.has_facility_details === "boolean"
          ? row.has_facility_details || !!facilityName || !!facilityAddress
          : !!facilityName || !!facilityAddress || !!consultancy,
    };
  });
}

async function fetchDoctorsBrowseFallback({
  search = "",
  verifiedOnly = false,
  selectedSpecialties = [],
  limit = 200,
} = {}) {
  const { data, error } = await supabase
    .from("doctor_profiles")
    .select("*")
    .limit(Number(limit) || 200);

  if (error) {
    if (looksLikePermissionDenied(error)) return [];
    return [];
  }

  let list = (Array.isArray(data) ? data : []).filter(isDiscoverableDoctorProfile);
  const s = String(search || "").trim();

  if (verifiedOnly) {
    list = list.filter((row) => String(row?.verified_status || "").toLowerCase() === "verified");
  }

  if (s) {
    list = list.filter((row) => doctorMatchesSearch(row, s));
  }

  if (selectedSpecialties.length) {
    list = list.filter((row) =>
      selectedSpecialties.some((sel) =>
        doctorSpecialtyCandidates(row).some((candidate) =>
          specialtyMatches(candidate, sel)
        )
      )
    );
  }

  const roleFiltered = await filterToDoctorRoleRows(list);

  return roleFiltered.map((row) => {
    const derivedConsultationFee =
      row?.consultation_fee ?? pickFeeFromFeesObject(row?.consultation_fees);
    const derivedCurrency = pickCurrencyFromFeesObject(row?.consultation_fees) || "";

    return {
      doctor_user_id: row.user_id,
      full_name: row?.full_name || null,
      first_name: row?.first_name || null,
      last_name: row?.last_name || null,
      display_name: pickDoctorDisplayName(row),
      specialty: displaySpecialtyLabel(doctorSpecialtyCandidates(row)[0] || "Doctor"),
      primary_specialty: row?.primary_specialty || null,
      specialties: toArray(row?.specialties),
      specialization: toArray(row?.specialization),
      verified_status: row.verified_status || "pending",
      onboarding_completed: row?.onboarding_completed ?? false,
      staging_deferred_verification:
        Array.isArray(row?.verification_documents) &&
        row.verification_documents.some((doc) => doc?.staging_deferred === true),
      account_classification: row?.account_classification || null,
      exclude_from_public_discovery: row?.exclude_from_public_discovery ?? false,
      public_discovery_excluded: row?.public_discovery_excluded ?? false,
      profile_photo_path: row.profile_photo_path || null,
      profile_image_url: row.profile_image_url || row.profile_photo_url || row.avatar_url || null,
      years_of_experience: row.years_of_experience ?? null,
      years_experience: row.years_experience ?? null,
      rating: row?.rating ?? null,
      languages_spoken: row.languages_spoken ?? row.languages ?? null,
      consultation_fee: derivedConsultationFee ?? null,
      consultation_fees: row?.consultation_fees ?? null,
      phone: row?.phone || null,
      practice_mode: row?.practice_mode || null,
      payout_meta: row?.payout_meta || null,
      register_country: row?.register_country || null,
      operating_country: resolveDoctorOperatingCountry(row),
      service_label: derivedConsultationFee != null ? "Consultation" : "",
      service_price: derivedConsultationFee ?? null,
      service_currency: derivedCurrency,
      currency: derivedCurrency,
      is_demo: false,
      consultancy_id: null,
      facility_name: null,
      facility_address: null,
      facility_city: row?.city || null,
      facility_country: row?.country || resolveDoctorOperatingCountry(row) || null,
      patients_age: null,
      availability_text: null,
      insurance_policy: null,
      payment_terms: null,
      display_location: firstNonEmpty(row?.city, row?.country, resolveDoctorOperatingCountry(row), "—"),
      has_facility_details: false,
    };
  });
}

export async function fetchDoctorsDirectoryBrowse({
  search = "",
  verifiedOnly = false,
  specialty = "", // string OR array of strings
  limit = 200,
  onPrimaryRows,
} = {}) {
  const BYPASS = isBypassAuth();
  const primaryViewName = "v_doctor_directory_browse";
  const devViewName = "v_doctor_directory_browse_dev";

  // If not bypass, require session — but do NOT throw (return empty for dev UX)
  if (!BYPASS) {
    const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) return [];
    if (!sessionData?.session) return [];
  }

  const s = String(search || "").trim();

  const spArr = Array.isArray(specialty)
    ? specialty.map((x) => String(x || "").trim()).filter(Boolean)
    : [];

  const spStr = !Array.isArray(specialty) ? String(specialty || "").trim() : "";

  const buildBrowseQuery = (viewName) => {
    let q = supabase
      .from(viewName)
      .select("*")
      .order("is_demo", { ascending: true })
      .order("display_name", { ascending: true })
      .limit(Number(limit) || 200);

    if (s) {
      q = q.or(
        `display_name.ilike.%${s}%,specialty.ilike.%${s}%,facility_name.ilike.%${s}%,facility_city.ilike.%${s}%,facility_country.ilike.%${s}%,display_location.ilike.%${s}%`
      );
    }

    if (verifiedOnly) {
      q = q.eq("verified_status", "verified");
    }

    if (spStr) {
      q = q.ilike("specialty", `%${spStr}%`);
    }

    return q;
  };

  // The canonical browse view and the resilient profile fallback are
  // independent. Start both immediately, then expose the canonical rows as
  // soon as they settle while the defensive merge continues in the background.
  const primaryRequestStartedAt = typeof performance !== "undefined" ? performance.now() : 0;
  const primaryRequest = buildBrowseQuery(primaryViewName);
  const fallbackRequest = fetchDoctorsBrowseFallback({
    search: s,
    verifiedOnly,
    selectedSpecialties: [],
    limit,
  });

  const { data: primaryData, error: primaryError } = await primaryRequest;
  let viewRows = !primaryError && Array.isArray(primaryData) ? primaryData : [];

  if ((!viewRows.length || primaryError) && BYPASS) {
    const { data: devData } = await buildBrowseQuery(devViewName);
    viewRows = Array.isArray(devData) ? devData : [];
  }

const selectedSpecialties = spArr.length > 0 ? spArr : spStr ? [spStr] : [];

const primaryReadyRows = viewRows
  // The governed browse view is already the canonical public-discovery
  // boundary. Paint its minimum safe card fields immediately; service,
  // facility, availability, and map enrichment can replace them afterward.
  .filter(isCanonicalBrowseCardReady)
  .filter((row) =>
    !selectedSpecialties.length ||
    selectedSpecialties.some((selected) =>
      doctorSpecialtyCandidates(row).some((candidate) => specialtyMatches(candidate, selected))
    )
  );

if (typeof onPrimaryRows === "function") {
  onPrimaryRows(primaryReadyRows, {
    durationMs: Math.round(
      (typeof performance !== "undefined" ? performance.now() : 0) - primaryRequestStartedAt
    ),
    primaryRequestCount: 1,
    primaryError: Boolean(primaryError),
    source: "canonical_browse",
  });
}

const fallbackRows = await fallbackRequest;

if (!primaryReadyRows.length && typeof onPrimaryRows === "function") {
  const fallbackReadyRows = fallbackRows
    .filter(isCanonicalBrowseCardReady)
    .filter((row) =>
      !selectedSpecialties.length ||
      selectedSpecialties.some((selected) =>
        doctorSpecialtyCandidates(row).some((candidate) => specialtyMatches(candidate, selected))
      )
    );
  onPrimaryRows(fallbackReadyRows, {
    durationMs: Math.round(
      (typeof performance !== "undefined" ? performance.now() : 0) - primaryRequestStartedAt
    ),
    primaryRequestCount: 1,
    primaryError: Boolean(primaryError),
    source: "resilient_profile_fallback",
  });
}

const mergedRows = BYPASS
  ? mergeDoctorRows(viewRows, fallbackRows)
  : await filterToDoctorRoleRows(mergeDoctorRows(viewRows, fallbackRows));
const enrichedRows = await enrichDoctorRows(mergedRows);
const patientReadyRows = enrichedRows.filter(isReadyForPatientDiscovery);

if (!patientReadyRows.length) return [];

if (!selectedSpecialties.length) return patientReadyRows;

const filtered = patientReadyRows.filter((row) =>
  selectedSpecialties.some((selected) =>
    doctorSpecialtyCandidates(row).some((candidate) =>
      specialtyMatches(candidate, selected)
    )
  )
);

return filtered;
}
